import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import moment from 'moment';
import { DropdownObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import {
  DATE_TIME_WITHOUT_TIMEZONE_FORMAT,
  FilterService,
  FilterKind,
} from 'app/modules/collection/services/filter.service';
import { ComparisonFilterSet } from 'app/providers/bridge-api/dashboard.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { TimezoneService } from 'app/services/timezone.service';

export const dynamicDateFilterValidatorFn = (
  control: AbstractControl<ComparisonFilterSet>
): ValidationErrors | null => {
  if (
    !control.value?.dynamicDate?.rangeNumber ||
    control.value?.dynamicDate.rangeNumber < 1 ||
    control.value?.dynamicDate.rangeNumber > 60
  ) {
    return { invalid: true };
  }
};
@EnableClassTelemetry()
@Component({
  selector: 'app-comparison-filter-dynamic-date-item',
  imports: [AppSharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './comparison-filter-dynamic-date-item.component.html',
  styleUrls: ['./comparison-filter-dynamic-date-item.component.scss'],
})
export class ComparisonFilterDynamicDateItemComponent implements OnInit, OnChanges {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() allFilters: ComparisonFilterSet[];
  @Input() filters: ComparisonFilterSet;
  @Input() isEnabled: boolean;
  @Input() isReadOnly: boolean;
  @Input() field: string;
  @Input() filterKind: FilterKind;

  @Output() filtersChange = new EventEmitter();

  rangeTypes: DropdownObject[] = [
    { id: 'period', title: 'equivalent period' },
    { id: 'year', title: 'year' },
    { id: 'quarter', title: 'quarter' },
    { id: 'month', title: 'month' },
    { id: 'week', title: 'week' },
    { id: 'day', title: 'day' },
  ];
  rangeType = 'period';
  rangeNumberMin = 1;
  rangeNumberMax = 60;
  rangeNumber: number | string = this.rangeNumberMin;
  originalStartDate: any;
  originalEndDate: any;
  dynamicStartDate: Date;
  dynamicEndDate: Date;
  isOutOfBound = false;

  constructor(
    private filterService: FilterService,
    private timezoneService: TimezoneService
  ) {}

  ngOnInit() {
    this.filterValueSet();
  }

  ngOnChanges() {
    this.rangeNumber = this.filters?.dynamicDate?.rangeNumber ?? 1;
    this.rangeType = this.filters?.dynamicDate?.rangeType ?? 'period';
    this.getRegularDateValue();
  }

  private getRegularDateValue() {
    const origDateFilter = this.allFilters[0]?.and[0];
    if (origDateFilter) {
      if (origDateFilter.key === 'daterange') {
        const range = origDateFilter.value.split('/');
        this.originalStartDate = new Date(range[1]) as any;
        this.originalEndDate = new Date(range[2]) as any;
      } else {
        const dateOptions = this.filterService.generateRelativeRangeOptions();
        const foundOption = dateOptions.find((o) => o.id === origDateFilter.value);
        this.originalStartDate = foundOption.first;
        this.originalEndDate = foundOption.last;
      }
      this.setDynamicDate();
      this.filterValueSet();
    }
  }

  private setDynamicDate() {
    if (this.rangeType === 'period') {
      const dateDiff = Math.abs(this.originalEndDate - this.originalStartDate);
      const newStartDate = this.originalStartDate - 1 - dateDiff * Number(this.rangeNumber);
      this.dynamicStartDate = new Date(newStartDate);
      this.dynamicEndDate = new Date(newStartDate + dateDiff);
    } else {
      this.dynamicStartDate = moment(this.originalStartDate)
        .subtract(this.rangeNumber, this.rangeType as moment.DurationInputArg2)
        .toDate();
      this.dynamicEndDate = moment(this.originalEndDate)
        .subtract(this.rangeNumber, this.rangeType as moment.DurationInputArg2)
        .toDate();
    }
  }

  onRangeValueChanged(rangeValue: number | string) {
    this.rangeNumber = rangeValue !== '' ? +rangeValue : '';
    this.isOutOfBound = rangeValue !== '' && (+this.rangeNumber < 1 || +this.rangeNumber > 60);

    this.setDynamicDate();
    this.filterValueSet();
  }

  onRangeTypeChanged(rangeTypeId: string) {
    this.rangeType = rangeTypeId;
    this.setDynamicDate();
    this.filterValueSet();
  }

  private filterValueSet() {
    if (!this.isEnabled) {
      return;
    }
    this.filtersChange.emit(this.toFilter());
  }

  toFilter = () => {
    const firstDate = this.timezoneService
      .padToStart(this.dynamicStartDate)
      .format(DATE_TIME_WITHOUT_TIMEZONE_FORMAT);
    const lastDate = this.timezoneService
      .padToEnd(this.dynamicEndDate)
      .format(DATE_TIME_WITHOUT_TIMEZONE_FORMAT);
    const filterValue = `daterange/${firstDate}/${lastDate}`;
    const filterKey = 'daterange';

    return {
      and: [
        {
          analyticItem: this.field,
          value: filterValue,
          key: filterKey,
          kind: this.filterKind,
        },
      ],
      or: [],
      not: [],
      dynamicDate: { rangeNumber: this.rangeNumber, rangeType: this.rangeType },
    };
  };
}
